import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { workflowAssociationService, type WorkflowSuggestionDto } from '../services/workflowAssociationService';
import { workflowService, type Workflow } from '../services/workflowService';
import {
  SignatureTypeCode,
  type SignaturePlacementInputDto,
  type SignerInputDto,
} from '../services/documentSignatureService';
import { PdfSignaturePlacementEditor } from './Signature/PdfSignaturePlacementEditor';
import { SignatureExpiresAtField } from './Signature/SignatureExpiresAtField';
import { userService } from '../services/userService';
import { teamService } from '../services/teamService';
import { isSignatureStepType } from '../utils/workflowConstants';
import { formatWorkflowSuggestionBadge } from '../utils/workflowSourceLabels';
import { useToast } from './UI/ToastProvider';
import type { DocumentPlacementInput, SignerPlacementInput } from '../services/documentService';
import { parseSignatureExpiresAtInput } from '../utils/signatureExpiresAtUtils';

export interface WorkflowSuggestionApplyResult {
  skipWorkflow: boolean;
  manualWorkflowId?: string;
  versioningType: 'manual' | 'automatic';
  requiresSignature: boolean;
  signers?: SignerInputDto[];
  documentPlacements?: DocumentPlacementInput[];
  signatureExpiresAt?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  templateId?: string;
  fileExtension?: string;
  /** Primeiro PDF selecionado no upload, para posicionamento antes da persistência. */
  previewPdfFile?: File | null;
  /** Lista de PDFs selecionados no upload para placement por arquivo. */
  previewPdfFiles?: File[];
  folderId?: string;
  /**
   * Quando true, o cliente prioriza/sugere o preenchimento de workflow por padrão.
   * Quando false (padrão), o workflow é opcional e "não aplicar workflow" passa a ser o padrão.
   * O valor autoritativo vem da resposta de sugestão da API; esta prop serve como fallback inicial.
   */
  suggestWorkflowByDefault?: boolean;
  onConfirm: (result: WorkflowSuggestionApplyResult) => void;
}

const normGuid = (g: string | null | undefined): string | null => {
  if (!g) return null;
  const t = g.trim();
  if (!/^[0-9a-fA-F-]{36}$/.test(t)) return t.toLowerCase();
  return t.toLowerCase();
};

const extensionForSuggest = (templateId: string | undefined, fileExtension: string | undefined): string | undefined => {
  if (templateId) return undefined;
  if (!fileExtension) return undefined;
  const e = fileExtension.trim().toLowerCase();
  if (!e) return undefined;
  return e.startsWith('.') ? e : `.${e}`;
};

const SIGNATURE_TYPE_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: SignatureTypeCode.Electronic, label: 'Eletrônica' },
  { value: SignatureTypeCode.Certificate, label: 'Certificado digital' },
  { value: SignatureTypeCode.Handwritten, label: 'Manuscrita' },
];

interface SignerFormEntry {
  key: string;
  name: string;
  email: string;
  signatureType: number;
  isRequired: boolean;
  placement?: SignaturePlacementInputDto | null;
}

async function buildSignerEntriesFromSignatureAssignees(workflow: Workflow | undefined): Promise<SignerFormEntry[]> {
  if (!workflow?.steps?.length) {
    return [];
  }

  const signatureStep = workflow.steps.find((s) => isSignatureStepType(s.type));
  if (!signatureStep?.assignees?.length) {
    return [];
  }

  const userIdSet = new Set<string>();
  for (const a of signatureStep.assignees) {
    const uid = a.userId ? normGuid(a.userId) : null;
    if (uid) {
      userIdSet.add(uid);
    }

    const tid = a.teamId ? normGuid(a.teamId) : null;
    if (tid) {
      const team = await teamService.getTeam(tid);
      if (team?.members?.length) {
        for (const m of team.members) {
          const mid = typeof m === 'string' ? normGuid(m) : null;
          if (mid) {
            userIdSet.add(mid);
          }
        }
      }
    }
  }

  const entries: SignerFormEntry[] = [];
  const userIds = Array.from(userIdSet);
  for (let i = 0; i < userIds.length; i++) {
    const id = userIds[i];
    try {
      const u = await userService.getUser(id);
      const email = (u.email || '').trim();
      if (!email) {
        continue;
      }

      entries.push({
        key: crypto.randomUUID(),
        name: (u.name || '').trim() || email,
        email,
        signatureType: SignatureTypeCode.Electronic,
        isRequired: true,
      });
    } catch {
      continue;
    }
  }

  return entries;
}

const createEmptySigner = (): SignerFormEntry => ({
  key: crypto.randomUUID(),
  name: '',
  email: '',
  signatureType: SignatureTypeCode.Electronic,
  isRequired: true,
});

export const WorkflowSuggestionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  customerId,
  templateId,
  fileExtension: fileExtensionProp,
  previewPdfFile,
  previewPdfFiles,
  folderId,
  suggestWorkflowByDefault = false,
  onConfirm,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [suggestion, setSuggestion] = useState<WorkflowSuggestionDto | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [noWorkflow, setNoWorkflow] = useState(false);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [visibleStep, setVisibleStep] = useState<1 | 2 | 3>(1);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [activePlacementKey, setActivePlacementKey] = useState<string | null>(null);
  const [activePlacementFileName, setActivePlacementFileName] = useState<string>('');
  const [placementsByFileAndSigner, setPlacementsByFileAndSigner] = useState<Record<string, Record<string, SignaturePlacementInputDto | null>>>({});
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [versioningType, setVersioningType] = useState<'manual' | 'automatic'>('automatic');
  const [signers, setSigners] = useState<SignerFormEntry[]>([]);
  const [signatureExpiresAt, setSignatureExpiresAt] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const firstSignerNameInputRef = useRef<HTMLInputElement>(null);
  const signatureExpiresAtInputRef = useRef<HTMLInputElement>(null);
  const signatoriesStepPrevRef = useRef<1 | 2>(1);
  const step1PanelRef = useRef<HTMLDivElement>(null);
  const step2PanelRef = useRef<HTMLDivElement>(null);
  const step3PanelRef = useRef<HTMLDivElement>(null);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  const fileExtension = extensionForSuggest(templateId, fileExtensionProp);
  const normalizedPreviewPdfFiles = useMemo(() => {
    if (previewPdfFiles && previewPdfFiles.length > 0)
    {
      return previewPdfFiles;
    }

    if (previewPdfFile)
    {
      return [previewPdfFile];
    }

    return [];
  }, [previewPdfFile, previewPdfFiles]);
  const activePreviewFile = useMemo(
    () => normalizedPreviewPdfFiles.find((f) => f.name === activePlacementFileName) ?? normalizedPreviewPdfFiles[0] ?? null,
    [activePlacementFileName, normalizedPreviewPdfFiles]
  );
  const isPdfUpload =
    !!fileExtension &&
    (fileExtension === '.pdf' || fileExtension === 'pdf' || fileExtension.endsWith('.pdf'));
  const showPdfPlacementStep = isPdfUpload && normalizedPreviewPdfFiles.length > 0 && !!previewBlobUrl;
  // eDoc: posicionamento só após persistência (SignaturePlacementModal), não na criação.
  const showPlacementStep = showPdfPlacementStep;

  const selectedWorkflow = useMemo(
    () => workflows.find((w) => w.id === selectedWorkflowId),
    [workflows, selectedWorkflowId]
  );

  const workflowHasSignatureStep = useMemo(() => {
    if (!selectedWorkflow) return false;
    return selectedWorkflow.steps.some((s) => isSignatureStepType(s.type));
  }, [selectedWorkflow]);

  const signatureForced = workflowHasSignatureStep;

  useEffect(() => {
    if (signatureForced) {
      setRequiresSignature(true);
    }
  }, [signatureForced]);

  const effectiveRequiresSignature = signatureForced || requiresSignature;

  const showSignatureExpiresAtField = effectiveRequiresSignature && !workflowHasSignatureStep;

  const validSigners = useMemo(
    () => signers.filter((s) => s.name.trim() && s.email.trim()),
    [signers]
  );

  const effectiveSuggestWorkflowByDefault =
    suggestion?.customerSuggestWorkflowByDefault ?? suggestWorkflowByDefault;

  const hasWorkflow = !!selectedWorkflowId && !noWorkflow;

  const versioningLocked = hasWorkflow;
  const effectiveVersioningType: 'manual' | 'automatic' = versioningLocked ? 'automatic' : versioningType;

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setLoadingWorkflows(true);
    setSuggestion(null);
    try {
      const [sug, wfs] = await Promise.all([
        workflowAssociationService.suggestWorkflow({
          customerId,
          templateId,
          fileExtension,
          folderId,
        }),
        workflowService.getWorkflowsByCustomer(customerId),
      ]);
      setSuggestion(sug);
      setWorkflows(wfs ?? []);
      const sw = sug?.workflowId && String(sug.workflowId) !== '00000000-0000-0000-0000-000000000000'
        ? String(sug.workflowId)
        : '';
      const suggestByDefault = sug?.customerSuggestWorkflowByDefault ?? suggestWorkflowByDefault;
      if (suggestByDefault)
      {
        setNoWorkflow(false);
        setSelectedWorkflowId(sw);
      }
      else
      {
        setNoWorkflow(true);
        setSelectedWorkflowId('');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao sugerir workflow';
      addToast(msg, 'error');
      setSuggestion(null);
      setWorkflows([]);
      setSelectedWorkflowId('');
    } finally {
      setLoading(false);
      setLoadingWorkflows(false);
    }
  }, [addToast, customerId, templateId, fileExtension, folderId, suggestWorkflowByDefault]);

  useEffect(() => {
    if (!activePreviewFile)
    {
      setPreviewBlobUrl(null);
      return;
    }

    const url = window.URL.createObjectURL(activePreviewFile);
    setPreviewBlobUrl(url);
    return () => {
      window.URL.revokeObjectURL(url);
    };
  }, [activePreviewFile]);

  useEffect(() => {
    if (normalizedPreviewPdfFiles.length === 0)
    {
      setActivePlacementFileName('');
      return;
    }

    const stillExists = normalizedPreviewPdfFiles.some((f) => f.name === activePlacementFileName);
    if (!stillExists)
    {
      setActivePlacementFileName(normalizedPreviewPdfFiles[0].name);
    }
  }, [activePlacementFileName, normalizedPreviewPdfFiles]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setVisibleStep(1);
      setSigners([]);
      setSignatureExpiresAt('');
      setRequiresSignature(false);
      setVersioningType('automatic');
      signatoriesStepPrevRef.current = 1;
      setNoWorkflow(false);
      setSelectedWorkflowId('');
      setActivePlacementKey(null);
      setActivePlacementFileName('');
      setPlacementsByFileAndSigner({});
      return;
    }

    setNoWorkflow(false);
    setSelectedWorkflowId('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    void load();
  }, [isOpen, customerId, templateId, fileExtension, folderId, load]);

  useEffect(() => {
    if (!isOpen)
    {
      return;
    }

    if (step === 1)
    {
      signatoriesStepPrevRef.current = 1;
      return;
    }

    if (step !== 2)
    {
      return;
    }

    if (signatoriesStepPrevRef.current === 2)
    {
      return;
    }

    if (signers.length === 0)
    {
      return;
    }

    signatoriesStepPrevRef.current = 2;

    const container = slideContainerRef.current;
    if (!container)
    {
      return;
    }

    const focusFirstInput = () => {
      requestAnimationFrame(() => {
        if (showSignatureExpiresAtField && signatureExpiresAtInputRef.current)
        {
          signatureExpiresAtInputRef.current.focus({ preventScroll: true });
        }
        else
        {
          firstSignerNameInputRef.current?.focus({ preventScroll: true });
        }
      });
    };

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'transform' && e.target === container)
      {
        focusFirstInput();
        container.removeEventListener('transitionend', handleTransitionEnd);
      }
    };

    container.addEventListener('transitionend', handleTransitionEnd);

    const fallbackId = window.setTimeout(() => {
      container.removeEventListener('transitionend', handleTransitionEnd);
      focusFirstInput();
    }, 400);

    return () => {
      container.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(fallbackId);
    };
  }, [isOpen, step, signers.length, showSignatureExpiresAtField]);

  useLayoutEffect(() => {
    const s1 = step1PanelRef.current;
    const s2 = step2PanelRef.current;
    const s3 = step3PanelRef.current;

    if (step === 1)
    {
      s1?.removeAttribute('inert');
      s1?.removeAttribute('aria-hidden');
      s2?.removeAttribute('inert');
      s2?.setAttribute('aria-hidden', 'true');
      s3?.removeAttribute('inert');
      s3?.setAttribute('aria-hidden', 'true');
    }
    else if (step === 2)
    {
      s1?.removeAttribute('inert');
      s1?.setAttribute('aria-hidden', 'true');
      s2?.removeAttribute('inert');
      s2?.removeAttribute('aria-hidden');
      s3?.removeAttribute('inert');
      s3?.setAttribute('aria-hidden', 'true');
    }
    else
    {
      s1?.removeAttribute('inert');
      s1?.setAttribute('aria-hidden', 'true');
      s2?.removeAttribute('inert');
      s2?.setAttribute('aria-hidden', 'true');
      s3?.removeAttribute('inert');
      s3?.removeAttribute('aria-hidden');
    }
  }, [step]);

  const requestClose = () => {
    if (!loading) onClose();
  };

  const hasSuggestedWorkflow =
    !!suggestion?.workflowId &&
    String(suggestion.workflowId) !== '00000000-0000-0000-0000-000000000000';

  const handleGoToStep2 = () => {
    if (!effectiveRequiresSignature) {
      handleFinalConfirm();
      return;
    }

    if (!noWorkflow && !selectedWorkflowId) {
      addToast('Selecione um workflow ou marque a opção de não aplicar workflow.', 'error');
      return;
    }

    void (async () => {
      try {
        if (signers.length === 0) {
          const wf = noWorkflow ? undefined : workflows.find((w) => w.id === selectedWorkflowId);
          const fromAssignees = await buildSignerEntriesFromSignatureAssignees(wf);
          setSigners(fromAssignees.length > 0 ? fromAssignees : [createEmptySigner()]);
        }

        setStep(2);
        setTimeout(() => setVisibleStep(2), 300);
      } catch {
        addToast('Não foi possível carregar os signatários sugeridos da etapa de assinatura.', 'error');
        setSigners([createEmptySigner()]);
        setStep(2);
        setTimeout(() => setVisibleStep(2), 300);
      }
    })();
  };

  const handleFinalConfirm = () => {
    if (!noWorkflow && !selectedWorkflowId) {
      addToast('Selecione um workflow ou marque a opção de não aplicar workflow.', 'error');
      return;
    }

    if (effectiveRequiresSignature && step === 1) {
      void (async () => {
        try {
          if (signers.length === 0) {
            const wf = noWorkflow ? undefined : workflows.find((w) => w.id === selectedWorkflowId);
            const fromAssignees = await buildSignerEntriesFromSignatureAssignees(wf);
            setSigners(fromAssignees.length > 0 ? fromAssignees : [createEmptySigner()]);
          }

          setStep(2);
          setTimeout(() => setVisibleStep(2), 300);
        } catch {
          addToast('Não foi possível carregar os signatários sugeridos da etapa de assinatura.', 'error');
          setSigners([createEmptySigner()]);
          setStep(2);
          setTimeout(() => setVisibleStep(2), 300);
        }
      })();
      return;
    }

    if (effectiveRequiresSignature && step === 2) {
      const validSigners = signers.filter((s) => s.name.trim() && s.email.trim());
      if (validSigners.length === 0) {
        addToast('Adicione ao menos um signatário.', 'error');
        return;
      }

      if (showPlacementStep) {
        const firstFileName = normalizedPreviewPdfFiles[0]?.name ?? '';
        const firstKey = validSigners[0]?.key ?? null;
        setActivePlacementFileName(firstFileName);
        setActivePlacementKey(firstKey);
        setStep(3);
        setTimeout(() => setVisibleStep(3), 300);
        return;
      }
    }

    const sug = hasSuggestedWorkflow ? normGuid(String(suggestion?.workflowId)) : null;
    const sel = normGuid(selectedWorkflowId);
    const needManual = !noWorkflow && (!sug || sug !== sel);

    const signerInputs: SignerInputDto[] | undefined = effectiveRequiresSignature
      ? signers
          .filter((s) => s.name.trim() && s.email.trim())
          .map((s) => ({
            name: s.name.trim(),
            email: s.email.trim(),
            signatureType: s.signatureType,
            isRequired: hasWorkflow ? true : s.isRequired,
            placement: s.placement ?? undefined,
          }))
      : undefined;

    const validSignerMap = signers.filter((s) => s.name.trim() && s.email.trim());
    const signerKeys = validSignerMap.map((s) => s.key);

    const missingPlacement = normalizedPreviewPdfFiles.find((file) =>
      signerKeys.some((signerKey) => !placementsByFileAndSigner[file.name]?.[signerKey])
    );
    if (effectiveRequiresSignature && step === 3 && showPdfPlacementStep && missingPlacement)
    {
      addToast(`Defina a posição de todos os signatários no arquivo "${missingPlacement.name}".`, 'error');
      return;
    }

    const documentPlacements: DocumentPlacementInput[] | undefined =
      effectiveRequiresSignature && normalizedPreviewPdfFiles.length > 0
        ? normalizedPreviewPdfFiles.map((file) => {
            const placementsForFile = placementsByFileAndSigner[file.name] ?? {};
            const placements: SignerPlacementInput[] = validSignerMap
              .map((signer) => {
                const placement = placementsForFile[signer.key];
                if (!placement)
                {
                  return null;
                }

                const signerKey = signer.email.trim().toLowerCase();
                return {
                  signerKey,
                  page: placement.page,
                  x: placement.x,
                  y: placement.y,
                  width: placement.width ?? 200,
                  height: placement.height ?? 60,
                };
              })
              .filter((item): item is SignerPlacementInput => item !== null);

            return {
              fileName: file.name,
              placements,
            };
          })
        : undefined;

    let signatureExpiresAtIso: string | null | undefined;
    if (showSignatureExpiresAtField)
    {
      const parsedExpiresAt = parseSignatureExpiresAtInput(signatureExpiresAt);
      if (!parsedExpiresAt.ok)
      {
        addToast(parsedExpiresAt.message, 'error');
        return;
      }

      signatureExpiresAtIso = parsedExpiresAt.iso;
    }

    onConfirm({
      skipWorkflow: noWorkflow,
      manualWorkflowId: noWorkflow ? undefined : needManual ? selectedWorkflowId : undefined,
      versioningType: effectiveVersioningType,
      requiresSignature: effectiveRequiresSignature,
      signers: signerInputs,
      documentPlacements,
      signatureExpiresAt: signatureExpiresAtIso,
    });
    onClose();
  };

  const handleAdvanceToNextSigner = () => {
    const currentIndex = validSigners.findIndex((s) => s.key === activePlacementKey);
    const currentSigner = validSigners[currentIndex];

    if (currentSigner)
    {
      const missingFile = normalizedPreviewPdfFiles.find(
        (file) => !placementsByFileAndSigner[file.name]?.[currentSigner.key]
      );
      if (missingFile)
      {
        addToast(
          `Defina a posição de "${currentSigner.name.trim()}" no arquivo "${missingFile.name}" antes de avançar.`,
          'error'
        );
        return;
      }
    }

    const nextSigner = validSigners[currentIndex + 1];
    if (nextSigner)
    {
      setActivePlacementKey(nextSigner.key);
      setActivePlacementFileName(normalizedPreviewPdfFiles[0]?.name ?? '');
    }
  };

  const handleSelectChange = (value: string) => {
    setNoWorkflow(false);
    setSelectedWorkflowId(value);
  };

  const handleNoWorkflowChange = (checked: boolean) => {
    setNoWorkflow(checked);
    if (checked) {
      setSelectedWorkflowId('');
    }
  };

  const addSigner = () => {
    setSigners((prev) => [...prev, createEmptySigner()]);
  };

  const removeSigner = (key: string) => {
    setSigners((prev) => prev.filter((s) => s.key !== key));
  };

  const updateSigner = (key: string, field: keyof SignerFormEntry, value: string | number | boolean) => {
    setSigners((prev) =>
      prev.map((s) => (s.key === key ? { ...s, [field]: value } : s))
    );
  };

  const Checkbox: React.FC<{
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
    disabled?: boolean;
  }> = ({ checked, onChange, label, disabled }) => (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
      className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-not-allowed opacity-60' : ''} text-gray-700`}
    >
      <span
        aria-hidden
        className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
          checked ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
        }`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="h-3 w-3 text-white" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3.5 8.5l2.5 2.5 6-6" />
          </svg>
        )}
      </span>
      {label}
    </button>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[80]" onClose={() => undefined}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                ref={panelRef}
                className={`w-full rounded-lg bg-white shadow-xl transition-all ${
                  visibleStep === 3 ? 'max-w-4xl' : 'max-w-2xl'
                }`}
              >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-gray-100">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {visibleStep === 1
                        ? 'Workflow do documento'
                        : visibleStep === 2
                          ? 'Configuração de signatários'
                          : 'Posicionamento no PDF'}
                    </Dialog.Title>
                    <div className="mt-3 flex items-center gap-2">
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${visibleStep === 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                          visibleStep > 1 ? 'bg-green-500 text-white' : visibleStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {visibleStep > 1 ? (
                            <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M3 8.5l3 3 7-7" />
                            </svg>
                          ) : '1'}
                        </span>
                        Workflow
                      </div>
                      {effectiveRequiresSignature && (
                        <>
                          <div className={`h-px w-12 transition-colors duration-300 ${visibleStep >= 2 ? 'bg-green-400' : 'bg-gray-200'}`} />
                          <div className={`flex items-center gap-1.5 text-xs font-medium ${visibleStep === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                              visibleStep > 2
                                ? 'bg-green-500 text-white'
                                : visibleStep === 2
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-500'
                            }`}>
                              {visibleStep > 2 ? (
                                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M3 8.5l3 3 7-7" />
                                </svg>
                              ) : '2'}
                            </span>
                            Signatários
                          </div>
                        </>
                      )}
                      {effectiveRequiresSignature && showPlacementStep && (
                        <>
                          <div className={`h-px w-12 transition-colors duration-300 ${visibleStep === 3 ? 'bg-green-400' : 'bg-gray-200'}`} />
                          <div className={`flex items-center gap-1.5 text-xs font-medium ${visibleStep === 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                              visibleStep === 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              3
                            </span>
                            Posicionar
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-1 text-gray-400 hover:text-gray-600 rounded p-0.5 hover:bg-gray-100 transition-colors"
                    onClick={requestClose}
                    disabled={loading}
                    aria-label="Fechar"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="overflow-x-hidden">
                  <div
                    ref={slideContainerRef}
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{
                      transform:
                        step === 1
                          ? 'translateX(0)'
                          : step === 2
                            ? 'translateX(-100%)'
                            : 'translateX(-200%)',
                    }}
                  >
                    {/* Step 1 */}
                    <div
                      ref={step1PanelRef}
                      className={`w-full flex-shrink-0 px-6 pt-5 pb-6 ${step !== 1 ? 'pointer-events-none' : ''}`}
                    >
                      {loading || loadingWorkflows ? (
                        <div className="flex items-center gap-2 py-6 justify-center text-sm text-gray-500">
                          <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Carregando workflows...
                        </div>
                      ) : (
                        <>
                          {suggestion?.workflowName && (
                            <div className="mb-4 flex items-start gap-2 rounded-md bg-blue-50 border border-blue-100 px-3 py-2.5">
                              <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                              </svg>
                              <div>
                                <p className="text-xs font-medium text-blue-700">Sugestão: {suggestion.workflowName}</p>
                                <p className="mt-0.5 text-xs text-blue-500">
                                  {formatWorkflowSuggestionBadge(suggestion.source, suggestion.ruleLabel, suggestion.workflowName)}
                                </p>
                              </div>
                            </div>
                          )}

                          <div>
                            <label htmlFor="workflow-suggestion-select" className="block text-sm font-medium text-gray-700">
                              {effectiveSuggestWorkflowByDefault ? 'Workflow da empresa' : 'Workflow da empresa (opcional)'}
                            </label>
                            {!effectiveSuggestWorkflowByDefault && (
                              <p className="mt-0.5 text-xs text-gray-500">
                                O preenchimento de workflow é opcional. Você pode confirmar sem selecionar um workflow.
                              </p>
                            )}
                            <select
                              id="workflow-suggestion-select"
                              className="mt-1.5 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                              value={noWorkflow ? '' : selectedWorkflowId}
                              onChange={(e) => handleSelectChange(e.target.value)}
                              disabled={noWorkflow}
                            >
                              <option value="">Selecione...</option>
                              {workflows.map((w) => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="mt-3">
                            <Checkbox
                              checked={noWorkflow}
                              onChange={handleNoWorkflowChange}
                              label="Não aplicar workflow neste documento"
                            />
                          </div>

                          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-gray-700">Tipo de versionamento</p>
                              {versioningLocked && (
                                <span
                                  className="inline-flex"
                                  title="Documentos com workflow usam versionamento automático. A opção manual só está disponível para documentos sem workflow."
                                >
                                  <InformationCircleIcon className="h-4 w-4 text-gray-400" aria-hidden />
                                </span>
                              )}
                            </div>
                            <div
                              role="radiogroup"
                              aria-label="Tipo de versionamento"
                              aria-readonly={versioningLocked}
                              className={`mt-2 flex gap-5 ${versioningLocked ? 'select-none opacity-90' : ''}`}
                            >
                              {(['automatic', 'manual'] as const).map((vt) => {
                                const checked = effectiveVersioningType === vt;
                                return (
                                  <button
                                    key={vt}
                                    type="button"
                                    role="radio"
                                    aria-checked={checked}
                                    aria-disabled={versioningLocked}
                                    disabled={versioningLocked}
                                    aria-label={vt === 'automatic' ? 'Automático' : 'Manual'}
                                    onClick={() => {
                                      if (!versioningLocked) {
                                        setVersioningType(vt);
                                      }
                                    }}
                                    className={`flex items-center gap-2 ${
                                      versioningLocked ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                  >
                                    <span
                                      aria-hidden
                                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                        checked ? 'border-blue-600' : 'border-gray-300'
                                      }`}
                                    >
                                      {checked && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                                    </span>
                                    <span className="text-sm text-gray-700">
                                      {vt === 'automatic' ? 'Automático' : 'Manual'}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="mt-4">
                            {signatureForced ? (
                              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5">
                                <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 11l6.364-6.364a2 2 0 012.828 2.828L11.828 13.828A4 4 0 019 15H7v-2a4 4 0 012-3.464z" />
                                </svg>
                                <p className="text-xs text-amber-700">
                                  O workflow selecionado possui etapa de assinatura. <strong>Assinatura obrigatória.</strong>
                                </p>
                              </div>
                            ) : (
                              <div className="rounded-md border border-gray-200 p-3">
                                <Checkbox
                                  checked={requiresSignature}
                                  onChange={setRequiresSignature}
                                  label="Exige assinatura digital"
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                          onClick={requestClose}
                          disabled={loading}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                          onClick={handleGoToStep2}
                          disabled={loading || loadingWorkflows}
                        >
                          {effectiveRequiresSignature ? 'Próximo →' : 'Confirmar'}
                        </button>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div
                      ref={step2PanelRef}
                      className={`w-full flex-shrink-0 px-6 pt-5 pb-6 ${step !== 2 ? 'pointer-events-none' : ''}`}
                    >
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-700">Adicione os signatários para este documento.</p>
                          {hasWorkflow && (
                            <p className="mt-1 text-xs text-amber-600">
                              Dentro de workflow, todos os signatários são obrigatórios.
                            </p>
                          )}
                        </div>

                        {showSignatureExpiresAtField && (
                          <SignatureExpiresAtField
                            ref={signatureExpiresAtInputRef}
                            id="workflow-suggestion-signature-expires-at"
                            value={signatureExpiresAt}
                            onChange={setSignatureExpiresAt}
                            disabled={loading}
                          />
                        )}

                        <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                        {signers.map((signer, idx) => (
                          <div key={signer.key} className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Signatário {idx + 1}
                              </span>
                              {signers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSigner(signer.key)}
                                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors"
                                  aria-label="Remover signatário"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                  Remover
                                </button>
                              )}
                            </div>
                            <div className="space-y-2">
                              <input
                                ref={idx === 0 ? firstSignerNameInputRef : undefined}
                                type="text"
                                id={idx === 0 ? 'workflow-suggestion-first-signer-name' : undefined}
                                autoComplete="name"
                                placeholder="Nome completo *"
                                value={signer.name}
                                onChange={(e) => updateSigner(signer.key, 'name', e.target.value)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <input
                                type="email"
                                placeholder="E-mail *"
                                value={signer.email}
                                onChange={(e) => updateSigner(signer.key, 'email', e.target.value)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-3">
                                <select
                                  value={signer.signatureType}
                                  onChange={(e) => updateSigner(signer.key, 'signatureType', Number(e.target.value))}
                                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  {SIGNATURE_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                {!hasWorkflow && (
                                  <Checkbox
                                    checked={signer.isRequired}
                                    onChange={(v) => updateSigner(signer.key, 'isRequired', v)}
                                    label="Obrigatório"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        </div>

                        <button
                          type="button"
                          onClick={addSigner}
                          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Adicionar signatário
                        </button>
                      </div>

                      <div className="mt-6 flex justify-between border-t border-gray-100 pt-4">
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50"
                          onClick={() => { setStep(1); setTimeout(() => setVisibleStep(1), 300); }}
                        >
                          ← Voltar
                        </button>
                        <button
                          type="button"
                          className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                          onClick={handleFinalConfirm}
                        >
                          {showPlacementStep ? 'Próximo →' : 'Confirmar'}
                        </button>
                      </div>
                    </div>

                    {/* Step 3 — posicionamento PDF */}
                    <div
                      ref={step3PanelRef}
                      className={`w-full flex-shrink-0 px-6 pt-5 pb-6 ${step !== 3 ? 'pointer-events-none' : ''}`}
                    >
                      <p className="mb-3 text-sm text-gray-600">
                        Defina onde cada signatário aparecerá em cada PDF do lote.
                      </p>
                      {showPdfPlacementStep ? (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {normalizedPreviewPdfFiles.map((file) => {
                            const completed = validSigners.every((s) => !!placementsByFileAndSigner[file.name]?.[s.key]);
                            return (
                              <button
                                key={file.name}
                                type="button"
                                onClick={() => setActivePlacementFileName(file.name)}
                                className={`rounded-md px-3 py-1 text-xs font-medium ${
                                  activePlacementFileName === file.name
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {file.name} {completed ? '✓' : ''}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                      {validSigners.length > 1 && (
                        <p className="mb-2 text-xs text-gray-500">
                          Signatário {validSigners.findIndex((s) => s.key === activePlacementKey) + 1} de {validSigners.length} — clique no documento para posicionar cada um.
                        </p>
                      )}
                      <div className="mb-3 flex flex-wrap gap-2">
                        {validSigners.map((s) => {
                          const fullyPlaced =
                            normalizedPreviewPdfFiles.length > 0 &&
                            normalizedPreviewPdfFiles.every((file) => !!placementsByFileAndSigner[file.name]?.[s.key]);
                          return (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => setActivePlacementKey(s.key)}
                              className={`rounded-md px-3 py-1 text-xs font-medium ${
                                activePlacementKey === s.key
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {s.name.trim()} {fullyPlaced ? '✓' : ''}
                            </button>
                          );
                        })}
                      </div>
                      {activePlacementKey && showPdfPlacementStep && previewBlobUrl ? (
                        <PdfSignaturePlacementEditor
                          previewBlobUrl={previewBlobUrl}
                          signerName={signers.find((s) => s.key === activePlacementKey)?.name ?? ''}
                          placement={placementsByFileAndSigner[activePlacementFileName]?.[activePlacementKey] ?? null}
                          onPlacementChange={(placement) => {
                            if (!activePlacementFileName || !activePlacementKey)
                            {
                              return;
                            }

                            setPlacementsByFileAndSigner((prev) => ({
                              ...prev,
                              [activePlacementFileName]: {
                                ...(prev[activePlacementFileName] ?? {}),
                                [activePlacementKey]: placement,
                              },
                            }));
                          }}
                        />
                      ) : null}
                      <div className="mt-6 flex justify-between border-t border-gray-100 pt-4">
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50"
                          onClick={() => { setStep(2); setTimeout(() => setVisibleStep(2), 300); }}
                        >
                          ← Voltar
                        </button>
                        {validSigners.length > 1 && validSigners.findIndex((s) => s.key === activePlacementKey) < validSigners.length - 1 ? (
                          <button
                            type="button"
                            className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                            onClick={handleAdvanceToNextSigner}
                          >
                            Avançar →
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                            onClick={handleFinalConfirm}
                          >
                            Confirmar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
